<template>
  <div
    v-if="layout.width > 0"
    class="bracket-stage"
    :style="stageStyle"
  >
    <svg class="bracket-lines" :width="layout.width" :height="layout.height">
      <line
        v-for="(line, i) in layout.lines"
        :key="i"
        :x1="line.x1"
        :y1="line.y1"
        :x2="line.x2"
        :y2="line.y2"
      />
    </svg>

    <div
      v-for="heading in layout.headings"
      :key="heading.round"
      class="round-heading text-body-2 font-weight-medium text-primary"
      :style="headingStyle(heading)"
    >
      {{ heading.label }}
    </div>

    <template v-for="race in roundOneRaces" :key="race.bracketSlot">
      <div
        class="bracket-row"
        :class="rowClass(race.homeSquad)"
        :style="rowStyle(race.xLeft, race.y1u - BRACKET.rowH)"
      >
        <span class="bracket-name">{{ race.homeSquad.name }}</span>
        <span v-if="race.homeSquad.score !== null" class="bracket-score">{{ race.homeSquad.score }}</span>
      </div>
      <div
        class="bracket-row"
        :class="rowClass(race.awaySquad)"
        :style="rowStyle(race.xLeft, race.y2u - BRACKET.rowH)"
      >
        <span class="bracket-name">{{ race.awaySquad.name }}</span>
        <span v-if="race.awaySquad.score !== null" class="bracket-score">{{ race.awaySquad.score }}</span>
      </div>
    </template>

    <div
      v-for="lane in laneLabels"
      :key="'lane-' + lane.bracketSlot"
      class="lane-label"
      :class="laneClass(lane)"
      :style="laneStyle(lane.x, lane.y, lane.width)"
    >
      <span class="bracket-name">{{ lane.squad.name }}</span>
      <span v-if="lane.score !== null" class="bracket-score">{{ lane.score }}</span>
    </div>

    <div
      v-if="championName"
      class="lane-label bracket-champion"
      :style="champStyle"
    >
      <v-icon icon="mdi-trophy-variant-outline" size="small" :color="placeColors[1]" class="mr-1 champion-trophy" />
      <span class="bracket-name">{{ championName }}</span>
    </div>

    <div
      v-if="runnerUpName"
      class="lane-label bracket-runner-up"
      :style="runnerUpStyle"
    >
      <v-icon icon="mdi-trophy-variant-outline" size="small" :color="placeColors[2]" class="mr-1 champion-trophy" />
      <span class="bracket-name">{{ runnerUpName }}</span>
    </div>

    <template v-if="consolation">
      <div
        class="round-heading text-body-2 font-weight-medium text-primary"
        :style="thirdHeadingStyle"
      >
        3rd Place
      </div>

      <div
        class="bracket-row"
        :class="rowClass(consolation.homeSquad)"
        :style="rowStyle(layout.thirdPlace.xLeft, layout.thirdPlace.y1u - BRACKET.rowH)"
      >
        <span class="bracket-name">{{ consolation.homeSquad.name }}</span>
        <span v-if="consolation.homeSquad.score !== null" class="bracket-score">{{ consolation.homeSquad.score }}</span>
      </div>
      <div
        class="bracket-row"
        :class="rowClass(consolation.awaySquad)"
        :style="rowStyle(layout.thirdPlace.xLeft, layout.thirdPlace.y2u - BRACKET.rowH)"
      >
        <span class="bracket-name">{{ consolation.awaySquad.name }}</span>
        <span v-if="consolation.awaySquad.score !== null" class="bracket-score">{{ consolation.awaySquad.score }}</span>
      </div>

      <div
        class="lane-label"
        :style="thirdLaneStyle"
      >
        <template v-if="thirdWinner">
          <v-icon
            icon="mdi-trophy-variant-outline"
            size="small"
            :color="placeColors[3]"
            class="mr-1 champion-trophy"
          />
          <span class="bracket-name">{{ thirdWinner.name }}</span>
        </template>
      </div>
    </template>
  </div>
</template>

<script>
import { BRACKET, computeBracketLayout } from "@/utils/bracket-layout";
import { PLACE_COLORS } from "@/utils/league-constants";

export default {
  name: "bracket-viewer",
  props: {
    rounds: {
      type: Array,
      default: () => []
    },
    consolation: {
      type: Object,
      default: null
    }
  },
  computed: {
    BRACKET () {
      return BRACKET;
    },
    layout () {
      return computeBracketLayout(this.rounds, this.consolation);
    },
    raceBySlot () {
      const bySlot = new Map();
      for (const round of this.rounds) {
        for (const race of round.races) bySlot.set(race.bracketSlot, race);
      }
      return bySlot;
    },
    roundOneRaces () {
      return this.layout.races
        .filter(r => r.roundIndex === 0)
        .map((pos) => {
          const race = this.raceBySlot.get(pos.bracketSlot);
          if (!race) return null;
          return { ...pos, homeSquad: race.homeSquad, awaySquad: race.awaySquad };
        })
        .filter(Boolean);
    },
    laneLabels () {
      const numRounds = this.rounds.length;
      if (numRounds < 2) return [];
      return this.layout.races
        .filter(r => r.roundIndex < numRounds - 1)
        .map((pos) => {
          const race = this.raceBySlot.get(pos.bracketSlot);
          if (!race) return null;
          const squad = this.squadOf(race);
          const nextRound = this.rounds[pos.roundIndex + 1].races;
          const nextRace = nextRound[Math.floor(pos.index / 2)];
          const nextConnX = this.layout.races.find(t => t.roundIndex === pos.roundIndex + 1).xRight;
          return {
            bracketSlot: pos.bracketSlot,
            x: pos.xRight + BRACKET.laneStub,
            y: pos.mid,
            width: nextConnX - pos.xRight - BRACKET.laneStub,
            squad,
            score: this.scoreInRace(squad, nextRace),
            bold: this.wonInRace(squad, nextRace)
          };
        })
        .filter(Boolean);
    },
    championName () {
      const final = this.rounds[this.rounds.length - 1];
      const race = final && final.races[0];
      if (!race) return null;
      const squad = this.squadOf(race);
      return squad && squad.winner === true ? squad.name : null;
    },
    runnerUpName () {
      const final = this.rounds[this.rounds.length - 1];
      const race = final && final.races[0];
      if (!race) return null;
      const loser = [race.homeSquad, race.awaySquad].find(s => s.winner === false);
      return loser ? loser.name : null;
    },
    thirdWinner () {
      if (!this.consolation) return null;
      const winner = [this.consolation.homeSquad, this.consolation.awaySquad].find(s => s.winner === true);
      return winner || null;
    },
    placeColors () {
      return PLACE_COLORS;
    },
    stageStyle () {
      return {
        width: `${this.layout.width}px`,
        height: `${this.layout.height}px`
      };
    },
    champStyle () {
      const champ = this.layout.champion;
      return {
        left: `${champ.x + BRACKET.laneStub}px`,
        top: `${champ.mid - BRACKET.rowH}px`,
        width: `${champ.lineEnd - champ.x - BRACKET.laneStub}px`,
        height: `${BRACKET.rowH}px`
      };
    },
    runnerUpStyle () {
      const champ = this.layout.champion;
      return {
        left: `${champ.x + BRACKET.laneStub}px`,
        top: `${champ.mid}px`,
        width: `${champ.lineEnd - champ.x - BRACKET.laneStub}px`,
        height: `${BRACKET.rowH}px`
      };
    },
    thirdHeadingStyle () {
      const third = this.layout.thirdPlace;
      const headings = this.layout.headings;
      if (!third || !headings || headings.length === 0) return {};
      const heading = headings[headings.length - 1];
      return {
        left: `${heading.x}px`,
        top: `${third.y1u - BRACKET.rowH - BRACKET.headingH - BRACKET.headingGap}px`,
        width: `${heading.width}px`,
        height: `${BRACKET.headingH}px`
      };
    },
    thirdLaneStyle () {
      const third = this.layout.thirdPlace;
      if (!third) return {};
      return {
        left: `${third.xRight + BRACKET.laneStub}px`,
        top: `${third.mid - BRACKET.rowH}px`,
        width: `${this.layout.thirdWinner.lineEnd - third.xRight - BRACKET.laneStub}px`,
        height: `${BRACKET.rowH}px`
      };
    }
  },
  methods: {
    headingStyle (heading) {
      return {
        left: `${heading.x}px`,
        top: "0px",
        width: `${heading.width}px`,
        height: `${BRACKET.headingH}px`
      };
    },
    rowStyle (left, top) {
      return {
        left: `${left}px`,
        top: `${top}px`,
        width: `${BRACKET.boxW}px`,
        height: `${BRACKET.rowH}px`
      };
    },
    laneStyle (left, top, width) {
      return {
        left: `${left}px`,
        top: `${top - BRACKET.rowH}px`,
        width: `${width}px`,
        height: `${BRACKET.rowH}px`
      };
    },
    rowClass (squad) {
      if (squad.placeholder) {
        return "text-disabled";
      }
      if (squad.winner === true) {
        return "font-weight-bold";
      }
      if (squad.winner === false) {
        return "text-medium-emphasis";
      }
      return "";
    },
    laneClass (lane) {
      const squad = lane.squad;
      if (squad.placeholder) {
        return "text-disabled";
      }
      if (lane.bold) {
        return "font-weight-bold";
      }
      return "";
    },
    squadOf (race) {
      const winner = [race.homeSquad, race.awaySquad].find(s => s.winner === true);
      if (winner) return winner;
      const anyKnown = [race.homeSquad, race.awaySquad].find(s => s.winner !== null);
      if (anyKnown) return anyKnown;
      return { name: "TBD", winner: null, score: null, placeholder: true };
    },
    wonInRace (squad, race) {
      return [race.homeSquad, race.awaySquad].some(s => s.id === squad.id && s.winner === true);
    },
    raceDecided (race) {
      return [race.homeSquad, race.awaySquad].some(s => s.winner === true);
    },
    scoreInRace (squad, race) {
      if (!squad.id || !this.raceDecided(race)) return null;
      const side = [race.homeSquad, race.awaySquad].find(s => s.id === squad.id);
      return side ? side.score : null;
    }
  }
};
</script>

<style lang="css" scoped>
.bracket-stage {
  --bracket-line-color: color-mix(in srgb, rgb(var(--v-theme-on-surface)) calc(var(--v-medium-emphasis-opacity) * 100%), transparent);
  position: relative;
}

.bracket-lines {
  position: absolute;
  inset: 0;
  overflow: visible;
}

.bracket-lines line {
  stroke: var(--bracket-line-color);
  stroke-width: 1;
}

.round-heading {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
}

.bracket-row {
  position: absolute;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  box-sizing: border-box;
  padding: 0 10px;
  border-bottom: 1px solid var(--bracket-line-color);
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
}

.bracket-row .bracket-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.bracket-row .bracket-score {
  flex-shrink: 0;
  padding-bottom: 2px;
}

.lane-label {
  position: absolute;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  padding: 0 0 0 6px;
  font-size: 14px;
  white-space: nowrap;
  box-sizing: border-box;
}

.lane-label .bracket-name {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lane-label .bracket-score {
  flex-shrink: 0;
  padding-bottom: 2px;
  padding-right: 10px;
}

.bracket-score {
  font-size: 13px;
  color: inherit;
  margin-left: 8px;
}

.champion-trophy {
  padding-bottom: 6px;
}

.bracket-champion .bracket-name {
  margin-right: 4px;
}
</style>